import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Chip,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import {
    formatShortDate,
    formatDateTime,
    formatTime,
    formatAnswer,
} from "../../utils/ettReportFormat";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

// Lists the qb questions a student actually solved for one homework chapter, opened from a
// progress cell in PrepHomeworkMatrix. Windowed to the round on the server, so the count
// matches the cell's "solved this round". Fetches on open.
const PrepChapterSessionsModal = ({ open, onClose, uuid, studentId, roundId, chapterId, studentName, chapterName }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!open || !uuid || !studentId || !roundId || !chapterId) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(false);
        setData(null);
        Axios.get(
            `${BASE}/${uuid}/students/${studentId}/rounds/${roundId}/chapters/${chapterId}/sessions`,
            { headers: { Authorization: getAuthHeader() } }
        )
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((e) => { if (!cancelled) { console.error("Chapter sessions error:", e); setError(true); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, uuid, studentId, roundId, chapterId]);

    const sessions = data?.sessions || [];
    const title = chapterName || data?.chapter_name || "Chapter";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ pb: 0.5 }}>
                {title}{studentName ? ` — ${studentName}` : ""}
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
                ) : error ? (
                    <Typography color="error" sx={{ py: 3 }}>Couldn&apos;t load the solved questions. Please try again.</Typography>
                ) : data ? (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
                            <Chip
                                size="small"
                                color="primary"
                                label={`${data.solved_count} solved${data.target_questions ? ` / ${data.target_questions}` : ""}`}
                            />
                            {data.accuracy_pct !== null && data.accuracy_pct !== undefined && (
                                <Chip size="small" variant="outlined" label={`${data.accuracy_pct}% correct`} />
                            )}
                            <Chip size="small" variant="outlined" label={`${data.minutes_spent || 0} min`} />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                                {data.from_date ? `Practiced since ${formatShortDate(data.from_date)}` : "All-time"}
                                {data.finalized && data.due_date ? ` · through due ${formatShortDate(data.due_date)}` : ""}
                            </Typography>
                        </Box>

                        {sessions.length === 0 ? (
                            <Typography color="text.secondary" sx={{ py: 2 }}>
                                No questions solved in this window yet.
                            </Typography>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                                            <TableCell>#</TableCell>
                                            <TableCell>Question</TableCell>
                                            <TableCell align="center">Result</TableCell>
                                            <TableCell align="center">Answer</TableCell>
                                            <TableCell align="center">Correct</TableCell>
                                            <TableCell align="center">Time</TableCell>
                                            <TableCell align="center">Solved</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sessions.map((s, i) => (
                                            <TableRow key={s.id} hover>
                                                <TableCell>{i + 1}</TableCell>
                                                <TableCell sx={{ maxWidth: 280 }}>
                                                    <Tooltip title={s.content_preview || ""} arrow>
                                                        <Typography variant="caption" sx={{
                                                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                        }}>
                                                            {s.content_preview || "—"}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {s.result
                                                        ? <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
                                                        : <CancelIcon sx={{ color: "error.main", fontSize: 20 }} />}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {s.is_multiselect
                                                        ? formatAnswer(s.user_multiselect_answer, s.answer_type)
                                                        : formatAnswer(s.user_answer, s.answer_type)}
                                                </TableCell>
                                                <TableCell align="center">{formatAnswer(s.correct_answer, s.answer_type)}</TableCell>
                                                <TableCell align="center">{formatTime(s.time_user_spent)}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title={formatDateTime(s.created_at)} arrow>
                                                        <span>{formatShortDate(s.created_at)}</span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                            Repeat attempts are counted separately — qbank chapters cycle, so a question can appear more than once.
                        </Typography>
                    </>
                ) : (
                    <Typography color="text.secondary" sx={{ py: 3 }}>No data.</Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PrepChapterSessionsModal;
